import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  BoutonMint,
  LigneFichier,
} from '../components/ui/Primitives';
import { useTaillePage } from '../lib/useTaillePage';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { FormulaireInscription } from '../components/inscriptions/FormulaireInscription';
import { usePerimetre } from '../site/usePerimetre';
import {
  dossierComplet,
  lignesCompletes,
  useApprenants,
  useDossier,
  useEnregistrerEntretien,
  useModifierDossier,
  useRenvoyerLienAcces,
  type LigneApprenant,
  type PieceDossier,
} from '../lib/useApprenants';
import { useEnvoyerRelances, type CanalNotification } from '../lib/useRelances';
import { PASTILLE_PIECE } from '../lib/pastilles';
import { useToast } from '../components/ui/useToast';
import { useAuth } from '../auth/useAuth';
import { ErreurApi } from '../lib/api';
import { TOUS_LES_SITES } from '../site/context';
import { useSites } from '../lib/useSites';

/** Rendu d'une date à l'heure de La Réunion (les dates arrivent en UTC). */
/** Message affichable d'une erreur d'API, sans fuite de détail technique. */
function messageErreur(erreur: unknown, repli: string): string {
  return erreur instanceof ErreurApi ? erreur.message : repli;
}

/**
 * Écran « Inscriptions & entretiens » du pôle administratif (CDC §4.1).
 * Structure et classes reprises de la maquette validée.
 */
export function AdmInscriptionsPage(): JSX.Element {
  const { site } = usePerimetre();
  const naviguer = useNavigate();
  const { data: sites } = useSites();
  const [mineur, setMineur] = useState(false);
  const [rqth, setRqth] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [inscriptionOuverte, setInscriptionOuverte] = useState(false);

  // Sur « Tous les sites », l'antenne n'est présélectionnée que s'il n'y en a
  // qu'une dans le périmètre : sinon le choix revient à la personne qui saisit.
  const siteParDefaut =
    site !== TOUS_LES_SITES ? site : (sites?.sites.length === 1 ? sites.sites[0]!.code : '');

  const filtres = {
    site,
    ...(mineur ? { mineur: true } : {}),
    ...(rqth ? { rqth: true } : {}),
    ...(recherche.trim() ? { recherche: recherche.trim() } : {}),
  };

  const { data, isLoading, error } = useApprenants(filtres);
  const entretien = useEnregistrerEntretien();
  const lienAcces = useRenvoyerLienAcces();
  const modifier = useModifierDossier();
  const envoyerRelances = useEnvoyerRelances();
  const notifier = useToast();
  const { utilisateur, peutAcceder } = useAuth();

  const [relanceOuverte, setRelanceOuverte] = useState(false);
  const [canauxRelance, setCanauxRelance] = useState<CanalNotification[]>(['EMAIL', 'SMS']);

  function envoyerRelanceGlobale(): void {
    envoyerRelances.mutate(
      { cible: 'DOSSIER_INCOMPLET', canaux: canauxRelance, site },
      {
        onSuccess: (reponse) => {
          notifier(reponse.message);
          setRelanceOuverte(false);
        },
        onError: (erreur) =>
          notifier(messageErreur(erreur, 'La relance groupée n’a pas pu être envoyée.')),
      },
    );
  }

  const compteurs = data?.compteurs;
  const lignes = useMemo(() => lignesCompletes(data), [data]);

  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [site, mineur, rqth, recherche, taillePage]);

  const totalCandidats = lignes.length;
  const pagesCandidats = Math.max(1, Math.ceil(totalCandidats / taillePage));
  const lignesAffichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return lignes.slice(debut, debut + taillePage);
  }, [lignes, page, taillePage]);

  // Candidat de la fiche d'entretien et du suivi des pièces. Aucune
  // présélection : ouvrir un dossier écrit une ligne au journal d'accès, et
  // cette trace doit correspondre à un acte, pas au simple affichage de
  // l'écran.
  const [candidatId, setCandidatId] = useState('');
  const [besoins, setBesoins] = useState('');
  const [rechercheEntretien, setRechercheEntretien] = useState('');
  const [comboboxOuvert, setComboboxOuvert] = useState(false);
  const comboboxRef = useRef<HTMLDivElement>(null);

  const candidat = lignes.find((ligne) => ligne.id === candidatId) ?? null;

  const rechercheEntretienNormalisee = rechercheEntretien.trim().toLowerCase();
  const lignesEntretien = rechercheEntretienNormalisee
    ? lignes.filter((ligne) =>
        `${ligne.prenom} ${ligne.nom} ${ligne.email ?? ''}`
          .toLowerCase()
          .includes(rechercheEntretienNormalisee),
      )
    : lignes;

  // Le select natif filtré n'affichait aucune trace du filtre tant qu'on ne
  // le rouvrait pas au clic : la recherche semblait ne rien faire. La liste
  // ci-dessous s'ouvre dès la saisie pour montrer le résultat immédiatement.
  useEffect(() => {
    if (!comboboxOuvert) return;
    function surClicExterieur(evenement: MouseEvent) {
      if (!comboboxRef.current?.contains(evenement.target as Node)) setComboboxOuvert(false);
    }
    document.addEventListener('mousedown', surClicExterieur);
    return () => document.removeEventListener('mousedown', surClicExterieur);
  }, [comboboxOuvert]);

  // Un changement de filtre peut faire sortir le candidat choisi de la liste :
  // sans cette remise à zéro, la fiche resterait ouverte sur un dossier que
  // l'écran n'affiche plus.
  useEffect(() => {
    if (!candidatId || isLoading || !data) return;
    if (!lignesCompletes(data).some((ligne) => ligne.id === candidatId)) setCandidatId('');
  }, [candidatId, data, isLoading]);

  // Le compte rendu appartient au candidat affiché : le garder d'une fiche à
  // l'autre reviendrait à enregistrer les besoins de l'un sur le dossier de
  // l'autre.
  useEffect(() => {
    setBesoins('');
  }, [candidatId]);

  const dossier = useDossier(candidatId || undefined);
  const dossierCharge = dossierComplet(dossier.data);
  const pieces = dossierCharge?.pieces ?? [];

  function enregistrerIssue(resultat: 'RETENU' | 'NON_RETENU'): void {
    if (!candidat) return;
    const nom = `${candidat.prenom} ${candidat.nom}`;
    entretien.mutate(
      {
        id: candidat.id,
        resultat,
        ...(besoins.trim() ? { besoinsRecueillis: besoins.trim() } : {}),
      },
      {
        onSuccess: (reponse) => {
          if (resultat === 'NON_RETENU') {
            notifier(`Candidature de ${nom} classée sans suite.`);
            return;
          }
          notifier(
            reponse.lienEnvoye
              ? `Lien d’accès et liste des pièces adressés à ${nom}.`
              : `Entretien enregistré : ${nom} n’a ni e-mail ni téléphone, le lien n’a pas pu partir.`,
          );
        },
        onError: (erreur) =>
          notifier(messageErreur(erreur, "L’issue de l’entretien n’a pas pu être enregistrée.")),
      },
    );
  }

  function basculerRqth(): void {
    if (!candidat) return;
    modifier.mutate(
      { id: candidat.id, valeurs: { rqth: !candidat.rqth } },
      {
        onSuccess: () =>
          notifier(
            candidat.rqth
              ? 'Situation de handicap retirée du dossier.'
              : 'Situation de handicap notée : l’attestation RQTH est désormais attendue.',
          ),
        onError: (erreur) =>
          notifier(messageErreur(erreur, 'La situation du dossier n’a pas pu être modifiée.')),
      },
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <FormulaireInscription
        ouvert={inscriptionOuverte}
        onFermer={() => setInscriptionOuverte(false)}
        siteParDefaut={siteParDefaut}
        onCree={(id) => {
          setInscriptionOuverte(false);
          // Le dossier s'ouvre aussitôt : c'est là que se complètent les
          // champs laissés vides et que se déposent les pièces.
          naviguer(`/adm-dossier/${id}`);
        }}
      />

      <EnteteEcran
        titre="Inscriptions & entretiens"
        sousTitre="Inscription, entretien de recrutement, envoi du lien d’accès"
        actions={
          <>
            <BoutonSecondaire
              onClick={() => setRelanceOuverte(true)}
              disabled={!compteurs?.dossiersIncomplets}
            >
              Relancer les dossiers incomplets
              {compteurs?.dossiersIncomplets ? ` (${compteurs.dossiersIncomplets})` : ''}
            </BoutonSecondaire>
            {peutAcceder('adm-export') && (
              <BoutonSecondaire onClick={() => naviguer('/adm-export')}>Exporter</BoutonSecondaire>
            )}
            <BoutonPrincipal onClick={() => setInscriptionOuverte(true)}>
              + Nouvelle inscription
            </BoutonPrincipal>
          </>
        }
      />

      <Modale
        titre="Relance globale des dossiers incomplets"
        sousTitre={`${compteurs?.dossiersIncomplets ?? 0} dossier(s) avec des pièces manquantes, sur le périmètre affiché`}
        ouverte={relanceOuverte}
        onFermer={() => setRelanceOuverte(false)}
      >
        <div className="space-y-4 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            Envoie le message du scénario « Dossier incomplet » à chaque candidat concerné.
            Chaque envoi est tracé sur son dossier, canal par canal — un mail parti et un SMS
            refusé sur le même destinataire restent deux lignes distinctes.
          </p>

          <fieldset>
            <legend className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Canaux
            </legend>
            <div className="flex gap-2">
              {(['EMAIL', 'SMS'] as const).map((canal) => (
                <button
                  key={canal}
                  type="button"
                  aria-pressed={canauxRelance.includes(canal)}
                  onClick={() =>
                    setCanauxRelance((liste) =>
                      liste.includes(canal)
                        ? liste.filter((valeur) => valeur !== canal)
                        : [...liste, canal],
                    )
                  }
                  className={`min-w-0 flex-1 rounded-xl border py-2.5 text-sm font-semibold transition ${
                    canauxRelance.includes(canal)
                      ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                      : 'border-gray-200 bg-gray-50 text-codialis-gray'
                  }`}
                >
                  {canal === 'EMAIL' ? 'Mail' : 'SMS'}
                </button>
              ))}
            </div>
          </fieldset>

          {envoyerRelances.error instanceof ErreurApi && (
            <MessageErreur message={envoyerRelances.error.message} />
          )}

          <div className="flex justify-end gap-3">
            <BoutonSecondaire onClick={() => setRelanceOuverte(false)}>Annuler</BoutonSecondaire>
            <BoutonPrincipal
              onClick={envoyerRelanceGlobale}
              disabled={canauxRelance.length === 0 || envoyerRelances.isPending}
            >
              {envoyerRelances.isPending
                ? 'Envoi…'
                : `Relancer ${compteurs?.dossiersIncomplets ?? 0} dossier(s)`}
            </BoutonPrincipal>
          </div>
        </div>
      </Modale>

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Inscriptions du mois"
          valeur={compteurs?.inscriptionsDuMois ?? '—'}
          detail="dossiers créés depuis le 1er du mois"
          ton="navy"
        />
        <Compteur
          libelle="Entretiens à mener"
          valeur={compteurs?.entretiensAMener ?? '—'}
          detail="inscriptions et entretiens planifiés"
          ton="warm"
        />
        <Compteur
          libelle="Liens d’accès envoyés"
          valeur={compteurs?.liensAccesEnvoyes ?? '—'}
          detail="espaces candidats ouverts"
          ton="blue"
        />
        <Compteur
          libelle="Dossiers incomplets"
          valeur={compteurs?.dossiersIncomplets ?? '—'}
          detail="relance automatique tous les 15 jours"
          ton="red"
        />
      </div>

      {/* Bloc d'automatisation du CDC §4.1 */}
      <div className="grid grid-cols-1 gap-4 rounded-2xl bg-white p-4 sm:p-6 shadow-card lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-codialis-teal">
            Automatisation
          </div>
          <h2 className="font-display mt-1 text-lg font-bold text-codialis-navy">
            Candidat retenu → lien d’accès automatique
          </h2>
          <p className="mt-1 text-sm text-codialis-gray">
            Dès que l’entretien est validé, le candidat reçoit un lien de création de son
            espace, accompagné de la liste des pièces à déposer. Aucun envoi papier.
          </p>
        </div>
        <div className="rounded-xl bg-codialis-sand p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            État de l’expédition
          </div>
          <p className="mt-1 text-xs text-codialis-gray">
            Le lien est généré et tracé, puis expédié sur les canaux raccordés. L’écran des
            relances indique lesquels le sont, et ce qui n’a été que tracé.
          </p>
        </div>
      </div>

      <Carte
        titre="Candidats · suivi des entretiens et des pièces"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              aria-label="Rechercher un candidat"
              value={recherche}
              onChange={(evenement) => setRecherche(evenement.target.value)}
              placeholder="Nom, prénom, e-mail"
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-codialis-mint"
            />
            <FiltreBascule
              libelle="Mineur"
              actif={mineur}
              onBasculer={() => setMineur((valeur) => !valeur)}
            />
            <FiltreBascule
              libelle="RQTH"
              actif={rqth}
              onBasculer={() => setRqth((valeur) => !valeur)}
            />
          </div>
        }
        sansBordure
      >
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'La liste des candidats n’a pas pu être chargée.'
              }
            />
          </div>
        )}

        {isLoading && <Chargement libelle="Chargement des candidats…" />}

        {!isLoading && !error && (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[860px]">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Candidat', 'Formation visée', 'Site', 'Statut', 'Pièces', 'Situation', ''].map(
                    (colonne, index) => (
                      <th
                        key={colonne || index}
                        className="px-4 sm:px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                      >
                        {colonne || <span className="sr-only">Actions</span>}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 sm:px-6 py-10 text-center text-sm text-codialis-gray">
                      Aucun candidat ne correspond aux filtres actifs
                    </td>
                  </tr>
                )}
                {lignesAffichees.map((ligne) => (
                  <LigneCandidat
                    key={ligne.id}
                    ligne={ligne}
                    onRetenir={() =>
                      entretien.mutate({ id: ligne.id, resultat: 'RETENU' })
                    }
                    onRelancer={() => lienAcces.mutate(ligne.id)}
                    enCours={entretien.isPending || lienAcces.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && !isLoading && totalCandidats > 0 && (
          <Pagination
            page={page}
            pages={pagesCandidats}
            total={totalCandidats}
            affiches={lignesAffichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="candidat"
          />
        )}
      </Carte>

      {data && (
        <p className="text-xs text-codialis-gray">
          {data.total} candidat{data.total > 1 ? 's' : ''} dans le périmètre affiché. Le nombre
          de pièces attendues dépend de la situation de chacun : six pièces obligatoires, plus
          les justificatifs conditionnels (hébergement, RQTH, contrat précédent, France Travail).
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Carte
          titre="Fiche d'entretien de recrutement"
          actions={
            <div ref={comboboxRef} className="relative flex flex-wrap items-center gap-2">
              <input
                type="search"
                value={rechercheEntretien}
                onChange={(evenement) => {
                  setRechercheEntretien(evenement.target.value);
                  setCandidatId('');
                  setComboboxOuvert(true);
                }}
                onFocus={() => setComboboxOuvert(true)}
                onKeyDown={(evenement) => {
                  if (evenement.key === 'Escape') setComboboxOuvert(false);
                }}
                placeholder="Rechercher un candidat…"
                aria-label="Rechercher un candidat pour la fiche d’entretien"
                role="combobox"
                aria-expanded={comboboxOuvert}
                aria-controls="liste-candidats-entretien"
                aria-autocomplete="list"
                autoComplete="off"
                className="w-40 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-codialis-mint sm:w-48"
              />
              {comboboxOuvert && (
                <ul
                  id="liste-candidats-entretien"
                  role="listbox"
                  aria-label="Candidat de la fiche d’entretien"
                  className="absolute left-0 top-full z-10 mt-1 max-h-56 w-64 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 text-xs shadow-lg"
                >
                  {lignesEntretien.length === 0 && (
                    <li className="px-3 py-2 text-codialis-gray">Aucun candidat trouvé.</li>
                  )}
                  {lignesEntretien.map((ligne) => (
                    <li key={ligne.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={ligne.id === candidatId}
                        onClick={() => {
                          setCandidatId(ligne.id);
                          setRechercheEntretien(`${ligne.prenom} ${ligne.nom}`);
                          setComboboxOuvert(false);
                        }}
                        className={`block w-full px-3 py-2 text-left hover:bg-codialis-sand ${
                          ligne.id === candidatId
                            ? 'bg-codialis-sand font-semibold text-codialis-navy'
                            : 'text-codialis-navy'
                        }`}
                      >
                        {ligne.prenom} {ligne.nom}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          }
        >
          {!candidat && (
            <p className="px-4 sm:px-6 py-10 text-sm text-codialis-gray">
              Choisir un candidat de la liste ci-dessus pour saisir son entretien de recrutement.
              L’ouverture d’un dossier est tracée au journal d’accès.
            </p>
          )}

          {candidat && (
            <div className="space-y-4 p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="adminscriptions-candidat" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                    Candidat
                  </label>
                  <input id="adminscriptions-candidat"
                    readOnly
                    value={`${candidat.prenom} ${candidat.nom}`}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
                  />
                  <p className="mt-1 text-[11px] text-codialis-gray">
                    {candidat.formation ?? 'formation visée non renseignée'} · {candidat.siteCode}
                  </p>
                </div>
                <div>
                  <label htmlFor="adminscriptions-charge-e-de-recrutement" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                    Chargé(e) de recrutement
                  </label>
                  <input id="adminscriptions-charge-e-de-recrutement"
                    readOnly
                    value={`${utilisateur?.prenom ?? ''} ${utilisateur?.nom ?? ''}`.trim()}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
                  />
                  <p className="mt-1 text-[11px] text-codialis-gray">
                    L’entretien est enregistré au nom du compte connecté.
                  </p>
                </div>
              </div>
              <div>
                <label
                  htmlFor="besoins-recueillis"
                  className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
                >
                  Besoins recueillis
                </label>
                <textarea
                  id="besoins-recueillis"
                  rows={3}
                  value={besoins}
                  onChange={(evenement) => setBesoins(evenement.target.value)}
                  placeholder="Projet, mobilité, permis, contraintes horaires… Ce compte rendu sert au matching entreprise."
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {/* La minorité est recalculée depuis la date de naissance et la
                    date d'entrée : elle s'affiche, elle ne se cochera jamais. */}
                <span
                  className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-sm text-codialis-navy"
                  title="Calculée depuis la date de naissance et la date d’entrée"
                >
                  <input type="checkbox" aria-label="Personne mineure" checked={candidat.mineur} disabled readOnly className="rounded" />
                  Personne mineure
                </span>
                <label className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-sm text-codialis-navy">
                  <input
                    type="checkbox"
                    checked={candidat.rqth}
                    onChange={basculerRqth}
                    disabled={modifier.isPending}
                    className="rounded"
                  />
                  Situation de handicap (RQTH)
                </label>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <BoutonMint
                  onClick={() => enregistrerIssue('RETENU')}
                  disabled={entretien.isPending}
                >
                  Candidat retenu → envoyer le lien
                </BoutonMint>
                <BoutonSecondaire
                  onClick={() => enregistrerIssue('NON_RETENU')}
                  disabled={entretien.isPending}
                >
                  Non retenu
                </BoutonSecondaire>
              </div>
            </div>
          )}
        </Carte>

        <Carte
          titre="Pièces à fournir"
          actions={
            dossierCharge ? (
              <span className="text-[11px] text-codialis-gray">
                {dossierCharge.completude.deposees}/
                {dossierCharge.completude.attendues} déposées
              </span>
            ) : undefined
          }
        >
          {!candidatId && (
            <p className="px-4 sm:px-6 py-10 text-sm text-codialis-gray">
              L’état des pièces s’affiche pour le candidat choisi dans la fiche d’entretien.
            </p>
          )}

          {candidatId && dossier.isLoading && <Chargement libelle="Chargement des pièces…" />}

          {candidatId && dossier.error && (
            <div className="p-4 sm:p-6">
              <MessageErreur
                message={messageErreur(
                  dossier.error,
                  'Les pièces de ce dossier n’ont pas pu être chargées.',
                )}
              />
            </div>
          )}

          {pieces.map((piece) => (
            <LigneFichier
              key={piece.id}
              nom={piece.libelle}
              detail={detailPiece(piece)}
              pastille={PASTILLE_PIECE[piece.statut]}
            />
          ))}

          {candidatId && !dossier.isLoading && !dossier.error && (
            <div className="bg-codialis-mint/10 px-4 sm:px-6 py-3 text-xs text-codialis-navy">
              Les pièces sont déposées <strong>par le candidat depuis son espace</strong>. Tant
              qu'elles manquent, une relance mail/SMS est envoyée automatiquement. Le contrôle et
              le dépôt pour le compte du candidat se font depuis{' '}
              <Link to={`/adm-dossier/${candidatId}`} className="font-semibold text-codialis-sky hover:underline">
                son dossier
              </Link>
              .
            </div>
          )}
        </Carte>
      </div>
    </div>
  );
}

/**
 * Obligation et avancement d'une pièce, en une ligne. Le nombre de fichiers
 * n'apparaît que quand la pièce en attend plusieurs — « 1/1 » n'apprendrait
 * rien à personne.
 */
function detailPiece(piece: PieceDossier): string {
  const obligation = piece.obligatoire ? 'Obligatoire' : 'Selon la situation';
  if (piece.statut === 'REFUSEE' && piece.motifRefus) return `${obligation} · ${piece.motifRefus}`;
  if (piece.nombreAttendu > 1) {
    return `${obligation} · ${piece.fichiers.length}/${piece.nombreAttendu} fichiers`;
  }
  return obligation;
}

function FiltreBascule({
  libelle,
  actif,
  onBasculer,
}: {
  libelle: string;
  actif: boolean;
  onBasculer: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onBasculer}
      aria-pressed={actif}
      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
        actif
          ? 'bg-codialis-navy text-white'
          : 'border border-gray-200 text-codialis-gray hover:bg-gray-50'
      }`}
    >
      {libelle}
    </button>
  );
}

function LigneCandidat({
  ligne,
  onRetenir,
  onRelancer,
  enCours,
}: {
  ligne: LigneApprenant;
  onRetenir: () => void;
  onRelancer: () => void;
  enCours: boolean;
}): JSX.Element {
  const aRetenir = ligne.statut === 'INSCRIT' || ligne.statut === 'ENTRETIEN_PLANIFIE';
  const aRelancer =
    ligne.statut === 'RETENU' ||
    ligne.statut === 'DOCUMENTS_EN_ATTENTE' ||
    ligne.statut === 'DOSSIER_INCOMPLET';

  return (
    <tr className="transition hover:bg-gray-50">
      <td className="px-4 sm:px-6 py-3">
        <div className="text-sm font-semibold text-codialis-navy">
          {ligne.prenom} {ligne.nom}
        </div>
        <div className="text-[11px] text-codialis-gray">
          {ligne.age === null ? 'âge inconnu' : `${ligne.age} ans`}
        </div>
      </td>
      <td className="px-4 sm:px-6 py-3 text-sm text-codialis-navy">{ligne.formation ?? '—'}</td>
      <td className="px-4 sm:px-6 py-3 text-sm text-codialis-gray">{ligne.siteCode}</td>
      <td className="px-4 sm:px-6 py-3">
        <Pastille texte={ligne.statutLibelle} ton={ligne.statutTon} />
      </td>
      <td className="px-4 sm:px-6 py-3 text-sm text-codialis-navy">
        {ligne.pieces.deposees}/{ligne.pieces.attendues}
      </td>
      <td className="px-4 sm:px-6 py-3">
        <div className="flex flex-wrap gap-1">
          {ligne.mineur && <Pastille texte="Mineur" ton="warning" />}
          {ligne.rqth && <Pastille texte="RQTH" ton="info" />}
          {!ligne.mineur && !ligne.rqth && (
            <span className="text-xs text-codialis-gray">—</span>
          )}
        </div>
      </td>
      <td className="px-4 sm:px-6 py-3">
        <div className="flex items-center justify-end gap-2">
          {aRetenir && (
            <button
              type="button"
              onClick={onRetenir}
              disabled={enCours}
              className="rounded-lg bg-codialis-mint/20 px-3 py-1.5 text-xs font-semibold text-codialis-accent-texte transition hover:bg-codialis-mint/30 disabled:opacity-50"
            >
              Retenir
            </button>
          )}
          {aRelancer && (
            <button
              type="button"
              onClick={onRelancer}
              disabled={enCours}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50"
            >
              Relancer
            </button>
          )}
          <Link
            to={`/adm-dossier/${ligne.id}`}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-codialis-sky transition hover:underline"
          >
            Dossier
          </Link>
        </div>
      </td>
    </tr>
  );
}
